import {useMemo, useState} from 'react'
import {
  buildHierarchicalOfficeList,
  createOfficeOptionFilter,
  defaultGetOfficeId,
  defaultGetOfficeLabel,
  hasChildOffices,
  OfficeAccessor,
  OfficeOption,
} from 'utils/func/officeHierarchy'

interface UseOfficeHierarchyOptions {
  /** 사업소 코드 접근자. 기본값은 value ?? officeId */
  getId?: OfficeAccessor
  /** 사업소 이름 접근자. 검색·표시에 함께 쓰이며 기본값은 label ?? officeName */
  getLabel?: OfficeAccessor
}

/**
 * 사업소 선택 Autocomplete 를 계층형으로 만들어주는 훅.
 *
 * 반환값을 MUI Autocomplete 에 그대로 넘기면 된다.
 *
 * ```tsx
 * const office = useOfficeHierarchy(officeList)
 * <Autocomplete
 *   options={office.options}
 *   filterOptions={office.filterOptions}
 *   renderOption={office.renderOption}
 *   getOptionLabel={(o: any) => o.officeName}
 *   ...
 * />
 * ```
 *
 * 본부(level 0) 아래 사업소(level 1)는 항상 노출되고, 상수/지하수 같은
 * 하위(level 2)는 우측 ▼ 를 눌러 펼쳤을 때만 보인다. 검색 시에는
 * 펼침 여부와 무관하게 매칭 항목과 그 상·하위가 함께 노출된다.
 */
const useOfficeHierarchy = (list: OfficeOption[], options: UseOfficeHierarchyOptions = {}) => {
  const {getId = defaultGetOfficeId, getLabel = defaultGetOfficeLabel} = options
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())

  const hierarchicalOfficeList = useMemo(
    () => buildHierarchicalOfficeList(list, {getId, getLabel}),
    [list]
  )

  const filterOptions = useMemo(
    () => createOfficeOptionFilter(expandedGroups, {getId, getLabel}),
    [expandedGroups]
  )

  const toggleGroup = (parentId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      next.has(parentId) ? next.delete(parentId) : next.add(parentId)
      return next
    })
  }

  const renderOption = (props: any, option: OfficeOption) => {
    const id = getId(option)
    const level = option.level ?? 0
    const isExpanded = expandedGroups.has(id)
    const hasChildren = level === 1 && hasChildOffices(hierarchicalOfficeList, id, getId)

    return (
      <li
        {...props}
        style={{
          paddingLeft: `${16 + level * 12}px`,
          fontWeight: level === 0 ? 'bold' : 'normal',
          fontSize: level === 0 ? '16px' : '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span>
          {level > 0 && <span style={{marginRight: 4, color: '#aaa'}}>{'└'}</span>}
          {getLabel(option)}
        </span>
        {hasChildren && (
          <span
            style={{
              marginLeft: 8,
              fontSize: '0.8rem',
              color: '#aaa',
              cursor: 'pointer',
              padding: '0 4px',
            }}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              toggleGroup(id)
            }}
          >
            {isExpanded ? '▲' : '▼'}
          </span>
        )}
      </li>
    )
  }

  return {options: hierarchicalOfficeList, filterOptions, renderOption, expandedGroups, toggleGroup}
}

export default useOfficeHierarchy
